import { SimpleDeferred } from '@tableau/vizportalclient-common-components';
import {
  DialogPositioningBehavior,
  DialogProps,
  DialogType,
  DialogWidget,
  HostedStackedElementContainer,
  OriginOptions,
  StackingRootContext
} from '@tableau/widgets';
import React from 'react';
import { Provider } from 'react-redux';
import { DismissDialogGeneric } from '../../app/actions/RequestFailedAction';
import { getAppStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';
import { TabDialogWidth } from './style/TabDialogWidth';


/**
 * These options are mostly a subset of the DialogProps interface of the shared widgets dialog.
 * See their documentation for descriptions (http://design/widgets/widget-library/dialog).
 */
export interface ReactModalOptions {
  type: DialogType;
  dialogContent: React.ReactElement; // the content of the dialog
  testId?: string;
  title?: string; // this really should be required but there are too many dialogs to update all of these at once
  showCloseButton?: boolean;
  positioningBehavior?: DialogPositioningBehavior; // does not support Anchored
  dialogStyle?: React.CSSProperties;
  dialogContentStyle?: React.CSSProperties;
  dialogBodyStyle?: React.CSSProperties;
  dialogWidth?: TabDialogWidth; // defaults to 400px
  footerContent?: React.ReactNode;
  dismissDialogOnCloseAction?: boolean; // Call dismiss() instead of close() on close request or glass click
  shouldNotCloseOnGlassClick?: boolean;
  shouldReturnFocusOnClose?: boolean;
  shouldNotSetDefaultWidth?: boolean;
  shouldRecenterOnUpdate?: boolean;
  disableAutoFocus?: DialogProps['disableAutoFocus'];
  getAnchorClientRect?: () => ClientRect;
  anchorOrigin?: OriginOptions;
}

export interface ModalInstance<R = undefined> {
  close: (result?: R) => void;
  dismiss: (reason?: any) => void;
  result: Promise<R>;
}

/**
 * The function that gets passed into `openCustom`.
 */
export type CreateCustomDialogFn<R = undefined> = (closeAndDismiss: CloseAndDismiss<R>) => React.ReactElement;

export type CloseAndDismiss<R = undefined> = Pick<ModalInstance<R>, 'close' | 'dismiss'>;

/**
 * Factory for creating stacking containers. It's extracted so we can stub it in unit tests,
 * because dialogs rendered using the stacking container can't be easily accessed via Enzyme.
 */
export namespace HostedStackedElementContainerFactory {
  export function newStackedElementContainer() {
    return new HostedStackedElementContainer();
  }
}


/**
 * A service that opens React shared-widgets modal dialogs.
 *
 * In React, the usual way to instantiate some UI element (for example, dialogs) is to make that
 * element a child of some other element further up the component tree. The parent component controls
 * when the dialog gets opened and closed, and information can be passed to and from the dialog via
 * props. **This service is not meant to facilitate such use cases,** although a `registerExistingModal`
 * function is provided to allow modals hosted elsewhere to automatically be closed when the app's
 * URL changes.
 *
 * This service is meant for dialogs that _don't_ have a parent component, such as those spawned
 * via menu actions, Redux Sagas, or other asynchronous workflows.
 */
export namespace ReactModalService {

  // The reference to this immutable array gets overwritten as dialogs are open or closed
  let modalInstances: ReadonlyArray<ModalInstance<unknown>> = [];

  /**
   * Like `ReactModalService.open`, but instead of passing in the options to create a dialog widget,
   * pass in a pre-created React Element. The Modal Service will mount it with the
   * HostedStackedElementContainer and return a ModalInstance with the same promise contract as
   * modals created with `ReactModalService.open`.
   *
   * This is useful in a few cases, including:
   * - Dialogs that are more customized than the usual props allow
   * - Dialogs that need to close themselves from within
   *
   * For buttons that have OK/Cancel buttons and performa an action, use `ReactActionDialog`
   * or `ReactConfirmationDialog` instead.
   *
   * @param createDialogElement A function provided by the user that returns the React element
   * to render. `close` and `dismiss` methods are passed into this function, which the element
   * can use (from within itself) to ask itself to be closed. **This should return a DialogWidget**
   * (or something that wraps a DialogWidget).
   */
  export function openCustom<R>(createDialogElement: CreateCustomDialogFn<R>, elementCloseHandler?: () => void): ModalInstance<R> {

    const deferredResult = new SimpleDeferred<R>();
    const hostContainer = HostedStackedElementContainerFactory.newStackedElementContainer();

    const closeDialog = () => {
      hostContainer && hostContainer.remove();
      elementCloseHandler && elementCloseHandler();
      modalInstances = modalInstances.filter(x => x !== modalInstance);
    };

    const modalInstance: ModalInstance<R> = {
      result: deferredResult.promise as Promise<any>,
      close: (result?: any) => {
        closeDialog();
        deferredResult.resolve(result);
      },
      dismiss: (reason?: any) => {
        closeDialog();
        deferredResult.reject(reason || DismissDialogGeneric);
      },
    };

    const customElement = createDialogElement(modalInstance);
    const wrappedDialog = wrapDialogInProviders(customElement);
    hostContainer.render(wrappedDialog);

    modalInstances = modalInstances.concat(modalInstance);
    return modalInstance;
  }

  /**
   * Opens a modal dialog with arbitrary content and properties, using a Promise interface.
   * The Promise interface allows the dialog to be used in an async workflow (e.g. a Redux saga):
   * `modalInstance.result` will resolve or reject after the dialog is closed or dismissed.
   * Since these dialogs don't have a conventional React parent, the ReactModalService will also
   * take care of dismissing the dialog when the app's URL changes.
   *
   * Modals opened using this method are stored internally via the `HostedStackedElementContainer`,
   * so they don't need to be the child of any existing React element.
   *
   * For dialogs with an OK/Cancel button, consider using `ReactActionDialog` or `ReactConfirmationDialog`,
   * which provides an interface tailored to that common use case.
   *
   * For customized dialogs, or those that need to be able to close themselves from within, use `openCustom`.
   */
  export function open<R>(opts: ReactModalOptions): ModalInstance<R> {
    return openCustom(closeAndDismiss => {
      const dialogProps = getDefaultDialogProps(opts, closeAndDismiss);
      return <DialogWidget {...dialogProps}>{opts.dialogContent}</DialogWidget>;
    });
  }

  /**
   * Builds the "typical" set of props for a DialogWidget that we use in modals across VizPortal Client
   * to provide a consistent look and feel.
   */
  export function getDefaultDialogProps<R>(opts: Omit<ReactModalOptions, 'dialogContent'>, closeAndDismiss: CloseAndDismiss<R>): DialogProps {

    const { close, dismiss } = closeAndDismiss;
    const closeOrDismiss = opts.dismissDialogOnCloseAction ? dismiss : close;

    const dialogWidth = opts.dialogWidth || (opts.shouldNotSetDefaultWidth ? undefined : 400);

    return {
      testId: opts.testId || 'modal',
      type: opts.type,
      title: opts.title || Messages.dialogTitle_tryToAvoidThis(),
      showCloseButton: opts.showCloseButton,
      onCloseRequest: closeOrDismiss,
      positioningBehavior: opts.positioningBehavior || DialogPositioningBehavior.Centered,
      isModal: true,
      handleGlassClick: !opts.shouldNotCloseOnGlassClick ? closeOrDismiss : undefined,
      dialogStyle: opts.dialogStyle,
      dialogContentStyle: opts.dialogContentStyle,
      dialogBodyStyle: opts.dialogBodyStyle,
      dialogWidth: dialogWidth,
      showFooter: !!opts.footerContent,
      footerContent: opts.footerContent,
      shouldAdjustPositionOnWindowResize: true,
      shouldReturnFocusOnClose: opts.shouldReturnFocusOnClose,
      disableAutoFocus: opts.disableAutoFocus,
      getAnchorClientRect: opts.getAnchorClientRect,
      anchorOrigin: opts.anchorOrigin
    };
  }

  /**
   * Allows dialogs to use the redux store and dispatch actions.
   *
   * The Provider is needed because the StackedElementContainer exists outside of
   * the <App /> which has its own <Provider>.
   *
   * The StackingRootContext is needed to instantiate the dialog in the #ng-app div for proper
   * CSS inheritance.
   */
  function wrapDialogInProviders(dialogElement: React.ReactElement) {
    const stackingRoot = document.getElementById('ng-app');
    return (
      <StackingRootContext.Provider value={stackingRoot}>
        <Provider store={getAppStore()}>
          { dialogElement }
        </Provider>
      </StackingRootContext.Provider>
    );
  }

  /**
   * Registers an object as a modal with the ReactModalService, so that it will
   * automatically have its `dismiss` method (or `close`, if `dismissed` is not
   * provided) called upon switching pages.
   *
   * Objects registered using this method should be hosted by themselves (e.g. as a child of an
   * existing component), and they are allowed to close themselves from outside ReactModalService.
   * The host component must be able to deal with the dialog being closed at any time.
   *
   * *Note*: Registering a hosted modal in this way is often not required, because host components
   * should unmount themselves if the URL changes. But, depending on the nature of the URL change
   * (e.g. navigate from Project A to Project B), some elements of the page may be reüsed, and this
   * function will be useful.
   *
   * @param partialModalInstance Functions that close or dismiss the dialog. `close` is required,
   *   `dismiss` is optional. Both should be no-ops if the dialog is already closed.
   */
  export function registerExistingModal(partialModalInstance: {
    close: ModalInstance<unknown>['close'];
    dismiss?: ModalInstance<unknown>['dismiss'];
  }) {
    const { close, dismiss } = partialModalInstance;
    modalInstances = modalInstances.concat({
      close: close,
      dismiss: dismiss || close,
      result: new SimpleDeferred().promise as Promise<any>,
    });
  }

  /**
   * Dismisses all modals currently registered with the service.
   * This function is intended to be called when the app's URL changes.
   */
  export function dismissAll(reason?: any) {
    modalInstances.forEach(m => m.dismiss(reason));
    modalInstances = [];
  }

  /**
   * @returns true if at least one modal dialog is open
   */
  export function isAnyModalOpen(): boolean {
    return modalInstances.length > 0;
  }
}
